import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'

type Language = 'ru' | 'en'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const translations = {
  ru: {
    // Navigation
    'nav.home': 'Главная',
    'nav.orders': 'Заказы',
    'nav.payment': 'Оплата',
    'nav.profile': 'Профиль',

    // Home Page
    'home.title': 'CoinsRubot',
    'home.subtitle': 'Пополнение монет через QR-код',
    'home.products': 'Наши продукты',
    'home.calculator': 'Калькулятор цены',

    // Products
    'product.tiktok_coins': 'Пополнение монет',
    'product.telegram_stars': 'Telegram Stars',
    'product.streaming': 'Steam',
    'product.itunes_cards': 'iTunes Cards',
    'product.coming_soon': 'Скоро',

    // Calculator
    'calculator.coins_quantity': 'Количество монет',
    'calculator.price_per_coin': 'RUB за монету',
    'calculator.total_price': 'Итоговая цена',
    'calculator.rub': 'RUB',

    // Form
    'form.tiktok_username': 'Имя пользователя',
    'form.telegram_username': 'Telegram username',
    'form.coins_quantity': 'Количество монет',
    'form.placeholder_tiktok': 'Введите имя пользователя',
    'form.placeholder_telegram': 'Введите ваш Telegram username',
    'form.placeholder_coins': 'Введите количество',

    // Buttons
    'btn.create_order': 'Создать заказ',
    'btn.pay': 'Оплатить',
    'btn.cancel': 'Отмена',
    'btn.confirm': 'Подтвердить',

    // Messages
    'msg.order_created': 'Заказ создан успешно',
    'msg.fill_all_fields': 'Пожалуйста, заполните все поля',
    'msg.invalid_quantity': 'Количество должно быть больше 0',
    'msg.coming_soon': 'Эта функция скоро будет доступна',

    // Orders Page
    'orders.title': 'Мои заказы',
    'orders.empty': 'У вас нет заказов',
    'orders.status': 'Статус',
    'orders.amount': 'Сумма',
    'orders.date': 'Дата',

    // Payment Page
    'payment.title': 'Оплата',
    'payment.method': 'Способ оплаты',
    'payment.card': 'Карта',
    'payment.wallet': 'Кошелёк',

    // Profile Page
    'profile.title': 'Профиль',
    'profile.user_info': 'Информация пользователя',
    'profile.balance': 'Баланс',
    'profile.language': 'Язык',
    'profile.settings': 'Настройки',

    // Profile Cashback & Referral
    'profile.cashback': 'Кэшбэк',
    'profile.cashback_desc': 'Кэшбэк 1% с каждого заказа',
    'profile.cashback_balance': 'Баланс кэшбэка',
    'profile.cashback_total': 'Всего накоплено',
    'profile.referral': 'Реферальная программа',
    'profile.referral_desc': 'Приглашайте друзей и получайте 10% с их заказов',
    'profile.referral_link': 'Ваша реферальная ссылка',
    'profile.referral_copy': 'Скопировать',
    'profile.referral_copied': 'Скопировано!',
    'profile.referral_count': 'Приглашено друзей',
    'profile.referral_earned': 'Заработано с рефералов',
    'profile.your_telegram': 'Ваш Telegram username',
    'profile.enter_telegram': 'Введите username для просмотра профиля',
    'profile.view_profile': 'Просмотр',
    'profile.orders_count': 'Заказов',
    'profile.total_spent': 'Потрачено',
    'profile.total_coins': 'Монет куплено',
    'profile.contact_support': 'Связаться с поддержкой',

    // Reviews
    'reviews.title': 'Отзывы',
    'reviews.write': '+ Написать отзыв',
    'reviews.cancel': 'Отмена',
    'reviews.form_title': 'Ваш отзыв',
    'reviews.your_name': 'Ваше имя',
    'reviews.name_placeholder': 'Как вас зовут?',
    'reviews.telegram': 'Telegram',
    'reviews.optional': 'необязательно',
    'reviews.rating': 'Оценка',
    'reviews.comment': 'Комментарий',
    'reviews.comment_placeholder': 'Расскажите о вашем опыте...',
    'reviews.submit': 'Отправить отзыв',
    'reviews.sending': 'Отправка...',
    'reviews.success': 'Отзыв успешно добавлен!',
    'reviews.error': 'Ошибка при отправке отзыва',
    'reviews.fill_required': 'Заполните обязательные поля',
    'reviews.empty': 'Отзывов пока нет',
    'reviews.be_first': 'Будьте первым, кто оставит отзыв!',

    // Customer-facing extras
    'home.working_hours': '🕙 Рабочее время: с 10:00 до 02:00 по московскому времени',
    'home.contact_title': 'Связаться с нами',
    'home.contact_text': 'Есть вопросы? Мы всегда готовы помочь — пишите нам в Telegram.',
    'home.contact_button': 'Написать в Telegram',
    'home.open_in_telegram': 'Откройте в Telegram для быстрого заказа',
    'home.open_in_telegram_desc': 'В боте ваш аккаунт определяется автоматически',
    'home.open_in_telegram_btn': 'Открыть →',
    'home.faq_title': 'Информация',

    // Order form
    'order.tg_username_prompt': 'Введите ваш Telegram username',
    'order.tg_username_desc': 'Нужен для связи и уведомлений о заказе. На этот @username вам придёт чат с поддержкой.',
    'order.tg_username_placeholder': 'ваш_username',
    'order.tg_username_invalid': 'Введите Telegram username (от 5 символов, латиница) или email.',
    'order.open_in_bot_alt': 'Или открыть в Telegram-боте',
    'order.login_method': 'Способ входа',
    'order.qr_code': 'QR-КОД',
    'order.credentials': 'ЛОГИН И ПАРОЛЬ',
    'order.tiktok_username': 'Имя пользователя',
    'order.coins_amount': '🪙 Количество монет',
    'order.amount_rub': '₽ Сумма оплаты',
    'order.will_pay': 'Заплачу, ₽',
    'order.will_get': 'Получу, монет',
    'order.bonus_coins': 'Бонусные монеты',
    'order.apply': 'Применить',
    'order.applied': '✓ Применён',
    'order.total_coins': 'Итого монет',
    'order.total_to_pay': 'Итого к оплате',
    'order.pay_button': '💳 Перейти к оплате',
    'order.creating': '⏳ Создаём заказ...',
    'order.enter_tg': 'Введите ваш Telegram username для связи и уведомлений',
    'order.enter_tiktok_user': 'Введите имя пользователя',
    'order.min_order': 'Минимальный заказ',
    'order.coins_word': 'монет',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.orders': 'Orders',
    'nav.payment': 'Payment',
    'nav.profile': 'Profile',

    // Home Page
    'home.title': 'CoinsRubot',
    'home.subtitle': 'Top Up Coins via QR Code',
    'home.products': 'Our Products',
    'home.calculator': 'Price Calculator',

    // Products
    'product.tiktok_coins': 'Coin Top-Up',
    'product.telegram_stars': 'Telegram Stars',
    'product.streaming': 'Steam',
    'product.itunes_cards': 'iTunes Cards',
    'product.coming_soon': 'Coming Soon',

    // Calculator
    'calculator.coins_quantity': 'Coins Quantity',
    'calculator.price_per_coin': '1.09 RUB per coin',
    'calculator.total_price': 'Total Price',
    'calculator.rub': 'RUB',

    // Form
    'form.tiktok_username': 'Username',
    'form.telegram_username': 'Telegram Username',
    'form.coins_quantity': 'Coins Quantity',
    'form.placeholder_tiktok': 'Enter username',
    'form.placeholder_telegram': 'Enter your Telegram username',
    'form.placeholder_coins': 'Enter quantity',

    // Buttons
    'btn.create_order': 'Create Order',
    'btn.pay': 'Pay',
    'btn.cancel': 'Cancel',
    'btn.confirm': 'Confirm',

    // Messages
    'msg.order_created': 'Order created successfully',
    'msg.fill_all_fields': 'Please fill in all fields',
    'msg.invalid_quantity': 'Quantity must be greater than 0',
    'msg.coming_soon': 'This feature will be available soon',

    // Orders Page
    'orders.title': 'My Orders',
    'orders.empty': 'You have no orders',
    'orders.status': 'Status',
    'orders.amount': 'Amount',
    'orders.date': 'Date',

    // Payment Page
    'payment.title': 'Payment',
    'payment.method': 'Payment Method',
    'payment.card': 'Card',
    'payment.wallet': 'Wallet',

    // Profile Page
    'profile.title': 'Profile',
    'profile.user_info': 'User Information',
    'profile.balance': 'Balance',
    'profile.language': 'Language',
    'profile.settings': 'Settings',

    // Profile Cashback & Referral
    'profile.cashback': 'Cashback',
    'profile.cashback_desc': '1% cashback on every order',
    'profile.cashback_balance': 'Cashback Balance',
    'profile.cashback_total': 'Total Earned',
    'profile.referral': 'Referral Program',
    'profile.referral_desc': 'Invite friends and earn 10% from their orders',
    'profile.referral_link': 'Your Referral Link',
    'profile.referral_copy': 'Copy',
    'profile.referral_copied': 'Copied!',
    'profile.referral_count': 'Friends Invited',
    'profile.referral_earned': 'Earned from Referrals',
    'profile.your_telegram': 'Your Telegram Username',
    'profile.enter_telegram': 'Enter username to view profile',
    'profile.view_profile': 'View',
    'profile.orders_count': 'Orders',
    'profile.total_spent': 'Spent',
    'profile.total_coins': 'Coins Bought',
    'profile.contact_support': 'Contact Support',

    // Reviews
    'reviews.title': 'Reviews',
    'reviews.write': '+ Write a Review',
    'reviews.cancel': 'Cancel',
    'reviews.form_title': 'Your Review',
    'reviews.your_name': 'Your Name',
    'reviews.name_placeholder': 'What is your name?',
    'reviews.telegram': 'Telegram',
    'reviews.optional': 'optional',
    'reviews.rating': 'Rating',
    'reviews.comment': 'Comment',
    'reviews.comment_placeholder': 'Tell us about your experience...',
    'reviews.submit': 'Submit Review',
    'reviews.sending': 'Sending...',
    'reviews.success': 'Review submitted successfully!',
    'reviews.error': 'Error submitting review',
    'reviews.fill_required': 'Please fill in required fields',
    'reviews.empty': 'No reviews yet',
    'reviews.be_first': 'Be the first to leave a review!',

    // Customer-facing extras
    'home.working_hours': '🕙 Working hours: 10:00 – 02:00 Moscow time',
    'home.contact_title': 'Contact us',
    'home.contact_text': 'Have questions? We are always ready to help — message us on Telegram.',
    'home.contact_button': 'Message on Telegram',
    'home.open_in_telegram': 'Open in Telegram for a faster order',
    'home.open_in_telegram_desc': 'In the bot your account is detected automatically',
    'home.open_in_telegram_btn': 'Open →',
    'home.faq_title': 'Information',

    // Order form
    'order.tg_username_prompt': 'Enter your Telegram username',
    'order.tg_username_desc': 'Needed for support chat and order notifications. Your chat will open at this @username.',
    'order.tg_username_placeholder': 'your_username',
    'order.tg_username_invalid': 'Enter a Telegram username (min 5 chars, latin) or an email address.',
    'order.open_in_bot_alt': 'Or open in the Telegram bot',
    'order.login_method': 'Login method',
    'order.qr_code': 'QR CODE',
    'order.credentials': 'LOGIN & PASSWORD',
    'order.tiktok_username': 'Username',
    'order.coins_amount': '🪙 Number of coins',
    'order.amount_rub': '₽ Amount to pay',
    'order.will_pay': "I'll pay, ₽",
    'order.will_get': "I'll get, coins",
    'order.bonus_coins': 'Bonus coins',
    'order.apply': 'Apply',
    'order.applied': '✓ Applied',
    'order.total_coins': 'Total coins',
    'order.total_to_pay': 'Total to pay',
    'order.pay_button': '💳 Go to payment',
    'order.creating': '⏳ Creating order...',
    'order.enter_tg': 'Enter your Telegram username for contact and notifications',
    'order.enter_tiktok_user': 'Enter username',
    'order.min_order': 'Minimum order',
    'order.coins_word': 'coins',
  },
}

const LANG_STORAGE_KEY = 'app-lang'

function detectInitialLanguage(): Language {
  // Russian-first product — always Russian (no EN toggle in the UI).
  return 'ru'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage)

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    try { window.localStorage.setItem(LANG_STORAGE_KEY, lang) } catch { /* ignore */ }
  }

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['ru']] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
